// External libraries
import React from 'react';
import { Checkbox as CheckboxPP } from '@paypalcorp/pp-react-checkbox-weasley-v7';
import { CheckboxV8, LegalConsent, LegalConsentV8 } from './StyledComponents';
import { features } from '@/lib';

// Internal
import type {
  GenericObjectType,
  InputOnBlurChange,
  InputOnChange,
  InputOnFocus,
} from '@/types';
import { PaypalThemedProvider } from '@/styles';

type CheckboxRestProps =
  | string
  | boolean
  | (() => void)
  | GenericObjectType
  | React.ReactNode;
interface CheckboxProps {
  blurHandler?: InputOnBlurChange;
  changeHandler:
    | InputOnChange
    | ((
        e: React.ChangeEvent<HTMLInputElement>,
        newValue: string | boolean,
        oldValue: string | boolean,
        metadata: GenericObjectType,
      ) => void);
  checked?: boolean;
  focusHandler?: InputOnFocus;
  id: string;
  label: React.ReactNode | string;
  name: string;
  required?: boolean;
  type?: string;
  isTermsCheckbox?: boolean;
  testID?: string;
  [x: string]: CheckboxRestProps;
}

// This function was lifted directly from react-components
const getValidation = (isChecked: boolean, isRequired: boolean) => {
  const valueMissing = isRequired && !isChecked;
  const errors = valueMissing ? ['valueMissing'] : [];
  return {
    errors,
    isValid: !valueMissing,
    valueMissing,
  };
};

export const Checkbox: React.FC<CheckboxProps> = ({
  blurHandler,
  changeHandler,
  checked,
  focusHandler,
  id,
  label,
  name,
  required = false,
  type,
  isTermsCheckbox,
  testID,
  ...rest
}) => {
  //this feature is specific to TH country only
  const isForcedGuest = features.isActive('shouldDisableSignup');
  const shouldUseRebrandStyles = features.isActive('hasRebrandColorTreatment');
  const customStyle = isForcedGuest ? 'ForcedGuestStyles' : '';
  const CheckboxComponent = shouldUseRebrandStyles ? CheckboxV8 : CheckboxPP;
  const LegalConsentComponent = shouldUseRebrandStyles
    ? LegalConsentV8
    : LegalConsent;
  const Component = isTermsCheckbox ? LegalConsentComponent : CheckboxComponent;
  return (
    <PaypalThemedProvider>
      <Component
        checked={checked}
        customStyle={customStyle}
        data-testid={testID}
        id={id}
        label={label as string}
        name={name}
        onBlur={blurHandler}
        onChange={e =>
          changeHandler(
            { ...e, target: { ...e.target, id, name, type: 'checkbox' } },
            !!e.target.checked,
            !e.target.checked,
            { validation: getValidation(e.target.checked, required) },
          )
        }
        onFocus={focusHandler}
        {...rest}
      />
    </PaypalThemedProvider>
  );
};
